import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { trackPageView } from "@/lib/analytics";
import { cleanupExpiredRecordings } from "@/lib/recordings-db";
import {
  QueryClient,
  QueryClientProvider,
  MutationCache,
  QueryCache,
} from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { useSession } from "@/lib/auth-client";
import { UserRole } from "@arna-med/shared";
import Login from "@/pages/Login";
import VerifyMagicLink from "@/pages/VerifyMagicLink";
import Dashboard from "@/pages/Dashboard";
import NewConsultation from "@/pages/NewConsultation";
import Consultation from "@/pages/Consultation";
import AdminUsers from "@/pages/AdminUsers";
import AdminPatients from "@/pages/AdminPatients";
import FacilitySettings from "@/pages/FacilitySettings";
import PatientPortal from "@/pages/PatientPortal";
import PatientConsultationView from "@/pages/PatientConsultationView";
import TemplateManager from "@/pages/TemplateManager";
import ReportStructures from "@/pages/ReportStructures";
import PromptManager from "@/pages/PromptManager";
import PatientDetail from "@/pages/PatientDetail";
import AuditLog from "@/pages/AuditLog";
import Profile from "@/pages/Profile";
import Report from "@/pages/Report";
import AuditDashboard from "@/pages/AuditDashboard";
import AuditReportDetail from "@/pages/AuditReportDetail";
import DoctorReportOverview from "@/pages/DoctorReportOverview";
import DrugDatabase from "@/pages/DrugDatabase";
import Departments from "@/pages/Departments";
import SystemLogs from "@/pages/SystemLogs";

/** Fires a page_view analytics event on every route change. Must be inside BrowserRouter. */
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

function getErrorMessage(err: unknown): string {
  const axiosErr = err as {
    response?: { data?: { error?: string } };
    message?: string;
  };
  return (
    axiosErr?.response?.data?.error ??
    axiosErr?.message ??
    "An unexpected error occurred"
  );
}

function shouldSuppressToast(err: unknown): boolean {
  const res = (err as { response?: { status?: number; data?: { code?: string; errorCode?: string } } })?.response;
  if (res?.status === 401) return true; // handled by useBackendAuthenticated (token refresh + redirect)
  if (res?.status === 422 && res?.data?.code === "MISSING_BLOCKING_FIELDS") return true; // handled inline in Consultation.tsx
  if (res?.status === 409 && res?.data?.errorCode === "PATIENT_ALREADY_EXISTS") return true; // handled inline in AdminPatients.tsx
  return false;
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => {
      if (!shouldSuppressToast(err)) toast.error(getErrorMessage(err));
    },
  }),
  mutationCache: new MutationCache({
    onError: (err) => {
      if (!shouldSuppressToast(err)) toast.error(getErrorMessage(err));
    },
  }),
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  if (isPending) return null;
  return session ? <>{children}</> : <Navigate to="/login" replace />;
}

function RequireOwnerOrManager({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  if (isPending) return null;
  if (!session) return <Navigate to="/login" replace />;
  const role = (session.user as { role?: string }).role as UserRole | undefined;
  if (role !== UserRole.Admin && role !== UserRole.Manager)
    return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function RequireOwner({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  if (isPending) return null;
  if (!session) return <Navigate to="/login" replace />;
  const role = (session.user as { role?: string }).role as UserRole | undefined;
  if (role !== UserRole.Admin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function RequireAuditor({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  if (isPending) return null;
  if (!session) return <Navigate to="/login" replace />;
  const role = (session.user as { role?: string }).role as UserRole | undefined;
  if (role !== UserRole.Auditor && role !== UserRole.Admin && role !== UserRole.Manager)
    return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function RequireNotAuditor({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  if (isPending) return null;
  if (!session) return <Navigate to="/login" replace />;
  const role = (session.user as { role?: string }).role as UserRole | undefined;
  if (role === UserRole.Auditor) return <Navigate to="/auditor" replace />;
  return <>{children}</>;
}

function RootRedirect() {
  const { data: session, isPending } = useSession();
  if (isPending) return null;
  if (!session) return <Navigate to="/login" replace />;
  const role = (session.user as { role?: string }).role as UserRole | undefined;
  if (role === UserRole.Auditor) return <Navigate to="/auditor" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  useEffect(() => {
    void cleanupExpiredRecordings();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <BrowserRouter>
          <RouteTracker />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/v1/verify" element={<VerifyMagicLink />} />
            <Route
              path="/dashboard"
              element={
                <RequireNotAuditor>
                  <Dashboard />
                </RequireNotAuditor>
              }
            />
            <Route
              path="/consultation/new"
              element={
                <RequireAuth>
                  <NewConsultation />
                </RequireAuth>
              }
            />
            <Route
              path="/consultation/:id"
              element={
                <RequireAuth>
                  <Consultation />
                </RequireAuth>
              }
            />
            <Route
              path="/patients"
              element={
                <RequireAuth>
                  <AdminPatients />
                </RequireAuth>
              }
            />
            <Route
              path="/patients/:id"
              element={
                <RequireAuth>
                  <PatientDetail />
                </RequireAuth>
              }
            />
            <Route
              path="/facility"
              element={
                <RequireOwner>
                  <FacilitySettings />
                </RequireOwner>
              }
            />
            <Route
              path="/portal"
              element={
                <RequireAuth>
                  <PatientPortal />
                </RequireAuth>
              }
            />
            <Route
              path="/portal/consultation/:id"
              element={
                <RequireAuth>
                  <PatientConsultationView />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/users"
              element={
                <RequireOwnerOrManager>
                  <AdminUsers />
                </RequireOwnerOrManager>
              }
            />
            {/* <Route
              path="/admin/analytics"
              element={
                <RequireOwnerOrManager>
                  <AdminAnalytics />
                </RequireOwnerOrManager>
              }
            /> */}
            <Route
              path="/templates"
              element={
                <RequireOwner>
                  <TemplateManager />
                </RequireOwner>
              }
            />
            <Route
              path="/admin/report-structures"
              element={
                <RequireOwner>
                  <ReportStructures />
                </RequireOwner>
              }
            />
            <Route
              path="/admin/prompts"
              element={
                <RequireOwner>
                  <PromptManager />
                </RequireOwner>
              }
            />
            <Route
              path="/admin/drugs"
              element={
                <RequireOwner>
                  <DrugDatabase />
                </RequireOwner>
              }
            />
            <Route
              path="/admin/logs"
              element={
                <RequireOwner>
                  <SystemLogs />
                </RequireOwner>
              }
            />
            <Route
              path="/admin/departments"
              element={
                <RequireOwnerOrManager>
                  <Departments />
                </RequireOwnerOrManager>
              }
            />
            <Route
              path="/admin/audit-log"
              element={
                <RequireAuditor>
                  <AuditLog />
                </RequireAuditor>
              }
            />
            <Route
              path="/profile"
              element={
                <RequireAuth>
                  <Profile />
                </RequireAuth>
              }
            />
            <Route
              path="/report/:id"
              element={
                <RequireAuth>
                  <Report />
                </RequireAuth>
              }
            />
            <Route
              path="/auditor"
              element={
                <RequireAuditor>
                  <AuditDashboard />
                </RequireAuditor>
              }
            />
            <Route
              path="/auditor/report/:id"
              element={
                <RequireAuditor>
                  <AuditReportDetail />
                </RequireAuditor>
              }
            />
            <Route
              path="/doctor/reports"
              element={
                <RequireAuth>
                  <DoctorReportOverview />
                </RequireAuth>
              }
            />
            <Route path="/" element={<RootRedirect />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
